import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  adminCheckAuth,
  adminLogout,
  fetchLeads,
  fetchQuizResults,
  fetchNurtureSummary,
  fetchBookings,
} from "@/lib/adminApi";
import { LogOut, Mail, MessageCircle, Loader2, Send, Calendar, CheckCircle2, XCircle, Download } from "lucide-react";
import { BRAND } from "@/lib/config";
import NewsletterBroadcast from "@/pages/AdminNewsletterBroadcast";
import AdminMetrics from "@/pages/AdminMetrics";
import AdminStories from "@/pages/AdminStories";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LEVEL_COLORS = {
  A1: "#9c9c9c",
  A2: "#5c8aa6",
  B1: "#1e2a5a",
  B2: "#7a4a8a",
  C1: "#d9381e",
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads"); // leads | quiz | nurture | bookings
  const [leads, setLeads] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [nurture, setNurture] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Verify the httpOnly cookie is still valid before loading data.
      const ok = await adminCheckAuth();
      if (!ok) {
        if (!cancelled) navigate("/admin", { replace: true });
        return;
      }
      try {
        const [l, q, n, b] = await Promise.all([
          fetchLeads(),
          fetchQuizResults(),
          fetchNurtureSummary(),
          fetchBookings(),
        ]);
        if (!cancelled) {
          setLeads(l);
          setQuiz(q);
          setNurture(n);
          setBookings(b);
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          await adminLogout();
          if (!cancelled) navigate("/admin", { replace: true });
          return;
        }
        if (!cancelled) setError("Erro ao carregar dados.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const onLogout = async () => {
    await adminLogout();
    navigate("/admin", { replace: true });
  };

  return (
    <div
      data-testid="admin-leads-page"
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]"
    >
      <header className="border-b border-[#e5e3db] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#1e2a5a] text-[#1e2a5a] font-display italic text-sm">
              L
            </span>
            <span className="font-display text-sm">
              {BRAND.name} · Painel
            </span>
          </Link>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-xs text-[#5c5c5c] hover:text-[#d9381e] transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="overline">Painel administrativo</p>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl tracking-tight">
          Leads & Resultados
        </h1>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Leads do formulário"
            value={leads?.length ?? "—"}
            testid="stat-leads-count"
          />
          <StatCard
            label="Agendamentos"
            value={
              bookings
                ? `${bookings.filter((b) => b.status === "confirmed").length} / ${bookings.length}`
                : "—"
            }
            testid="stat-bookings-count"
          />
          <StatCard
            label="Resultados do teste"
            value={quiz?.length ?? "—"}
            testid="stat-quiz-count"
          />
          <StatCard
            label="Nurture: enviados / pendentes"
            value={
              nurture
                ? `${nurture.total_sent} / ${nurture.total_pending}`
                : "—"
            }
            testid="stat-nurture-count"
          />
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          <TabButton
            active={tab === "leads"}
            onClick={() => setTab("leads")}
            count={leads?.length}
            testid="tab-leads"
          >
            Formulário de contato
          </TabButton>
          <TabButton
            active={tab === "bookings"}
            onClick={() => setTab("bookings")}
            count={bookings?.length}
            testid="tab-bookings"
          >
            Agendamentos
          </TabButton>
          <TabButton
            active={tab === "quiz"}
            onClick={() => setTab("quiz")}
            count={quiz?.length}
            testid="tab-quiz"
          >
            Teste de nível
          </TabButton>
          <TabButton
            active={tab === "nurture"}
            onClick={() => setTab("nurture")}
            count={nurture?.total_pending}
            testid="tab-nurture"
          >
            Sequência nurture
          </TabButton>
          <TabButton
            active={tab === "broadcast"}
            onClick={() => setTab("broadcast")}
            testid="tab-broadcast"
          >
            Newsletter · envio
          </TabButton>
          <TabButton
            active={tab === "metrics"}
            onClick={() => setTab("metrics")}
            testid="tab-metrics"
          >
            Métricas
          </TabButton>
          <TabButton
            active={tab === "stories"}
            onClick={() => setTab("stories")}
            testid="tab-stories"
          >
            Stories · 7 dias
          </TabButton>
        </div>

        {error && (
          <p className="mt-8 text-sm text-[#d9381e]">{error}</p>
        )}

        {tab === "leads" && (
          <LeadsTable leads={leads} />
        )}
        {tab === "bookings" && (
          <BookingsTable bookings={bookings} />
        )}
        {tab === "quiz" && (
          <QuizTable items={quiz} />
        )}
        {tab === "nurture" && (
          <NurtureSummary data={nurture} />
        )}
        {tab === "broadcast" && (
          <NewsletterBroadcast />
        )}
        {tab === "metrics" && (
          <AdminMetrics />
        )}
        {tab === "stories" && (
          <AdminStories />
        )}
      </main>
    </div>
  );
}

const StatCard = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#e5e3db] rounded-xl px-5 py-4"
  >
    <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">{label}</p>
    <p className="mt-2 font-display text-3xl text-[#1e2a5a]">{value}</p>
  </div>
);

const TabButton = ({ active, onClick, children, count, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
      active
        ? "bg-[#1e2a5a] text-[#f4f1ea]"
        : "border border-[#e5e3db] text-[#1e2a5a] hover:border-[#1e2a5a]"
    }`}
  >
    {children}
    {count !== undefined && count !== null ? (
      <span
        className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-[10px] font-medium ${
          active ? "bg-white/15 text-[#f4f1ea]" : "bg-[#f0efea] text-[#5c5c5c]"
        }`}
      >
        {count}
      </span>
    ) : null}
  </button>
);

const Loading = () => (
  <div className="mt-12 flex items-center gap-3 text-[#5c5c5c]">
    <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
  </div>
);

const EmptyState = ({ children }) => (
  <p className="mt-12 text-sm text-[#5c5c5c]">{children}</p>
);

const SOURCE_LABELS = {
  contact_form: "Formulário de contato",
  newsletter: "Newsletter",
  lead_magnet: "Lead magnet (PDF/PPT)",
  lead_magnet_50_expressoes: "Lead magnet · 50 expressões",
  lead_magnet_a1_premiers_pas: "Lead magnet · A1 Premiers pas",
  quiz: "Teste de nível",
  booking: "Agendamento",
};

const LeadsTable = ({ leads }) => {
  const [source, setSource] = useState("");
  if (leads === null) return <Loading />;
  if (leads.length === 0)
    return <EmptyState>Nenhum lead recebido ainda.</EmptyState>;

  const availableSources = Array.from(
    new Set(leads.map((l) => l.source || "contact_form"))
  );
  const filtered = source
    ? leads.filter((l) => (l.source || "contact_form") === source)
    : leads;

  const exportCsv = () => {
    const headers = [
      "Data",
      "Nome",
      "Email",
      "WhatsApp",
      "Origem",
      "Nivel",
      "Objetivo",
      "Mensagem",
      "Idioma",
    ];
    const escapeCell = (v) => {
      if (v === null || v === undefined) return "";
      const str = String(v).replace(/"/g, '""');
      return `"${str}"`;
    };
    const rows = filtered.map((l) => [
      l.created_at || "",
      l.name || "",
      l.email || "",
      l.phone || "",
      SOURCE_LABELS[l.source || "contact_form"] || l.source || "",
      l.level || "",
      l.interest || "",
      l.message || "",
      l.language || "pt",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCell).join(","))
      .join("\n");
    // BOM so Excel picks up UTF-8 correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const filterTag = source ? `_${source}` : "_todos";
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_npdlbr${filterTag}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label
          htmlFor="lead-source-filter"
          className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]"
        >
          Filtrar por origem
        </label>
        <select
          id="lead-source-filter"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          data-testid="lead-source-filter"
          className="rounded-full border border-[#e5e3db] bg-white px-4 py-1.5 text-xs sm:text-sm focus:border-[#1e2a5a] focus:outline-none"
        >
          <option value="">Todos ({leads.length})</option>
          {availableSources.map((s) => {
            const count = leads.filter((l) => (l.source || "contact_form") === s).length;
            return (
              <option key={s} value={s}>
                {SOURCE_LABELS[s] || s} ({count})
              </option>
            );
          })}
        </select>
        {source && (
          <button
            type="button"
            onClick={() => setSource("")}
            data-testid="lead-source-clear"
            className="text-xs text-[#5c5c5c] hover:text-[#d9381e] transition-colors link-underline"
          >
            Limpar filtro
          </button>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="lead-export-csv"
          className="inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-4 py-1.5 text-xs sm:text-sm font-medium text-[#f4f1ea] hover:bg-[#d9381e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-3.5 w-3.5" strokeWidth={2} />
          Exportar CSV ({filtered.length})
        </button>
      </div>
      <div className="overflow-x-auto border border-[#e5e3db] rounded-xl bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#5c5c5c] bg-[#f0efea]">
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Contato</th>
              <th className="px-4 py-3">Origem</th>
              <th className="px-4 py-3">Nível</th>
              <th className="px-4 py-3">Objetivo</th>
              <th className="px-4 py-3">Mensagem</th>
              <th className="px-4 py-3">Idioma</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="border-t border-[#e5e3db] align-top"
              >
                <td className="px-4 py-3 text-[#5c5c5c] whitespace-nowrap">
                  {formatDate(l.created_at)}
                </td>
                <td className="px-4 py-3 font-medium">
                  {l.name}
                  {l.preferred_name && (
                    <div className="mt-0.5 text-xs font-normal text-[#5c5c5c] italic">
                      « {l.preferred_name} »
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <a
                    href={`mailto:${l.email}`}
                    className="inline-flex items-center gap-1.5 link-underline"
                  >
                    <Mail className="h-3.5 w-3.5" /> {l.email}
                  </a>
                  {l.phone && (
                    <div className="mt-1 text-[#5c5c5c] flex items-center gap-1.5 text-xs">
                      <MessageCircle className="h-3 w-3" /> {l.phone}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center rounded-full bg-[#f0efea] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[#1e2a5a]">
                    {SOURCE_LABELS[l.source || "contact_form"] || l.source}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#5c5c5c]">{l.level || "—"}</td>
                <td className="px-4 py-3 text-[#5c5c5c]">{l.interest || "—"}</td>
                <td className="px-4 py-3 text-[#5c5c5c] max-w-xs">
                  {l.message || "—"}
                </td>
                <td className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c]">
                  {l.language || "pt"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <p className="mt-6 text-sm text-[#5c5c5c] italic" data-testid="lead-filter-empty">
          Nenhum lead nessa origem.
        </p>
      )}
    </div>
  );
};

const BookingStatusBadge = ({ status }) => {
  const map = {
    pending: { bg: "#f4f1ea", fg: "#5c5c5c", label: "Pendente", Icon: Loader2 },
    confirmed: { bg: "#e8f4ea", fg: "#2f7a3c", label: "Confirmado", Icon: CheckCircle2 },
    cancelled: { bg: "#fbeae7", fg: "#d9381e", label: "Recusado", Icon: XCircle },
  };
  const cfg = map[status] || map.pending;
  const { Icon } = cfg;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.fg }}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const BookingsTable = ({ bookings }) => {
  if (bookings === null) return <Loading />;
  if (bookings.length === 0)
    return <EmptyState>Nenhum agendamento recebido ainda.</EmptyState>;
  return (
    <div className="mt-8 overflow-x-auto border border-[#e5e3db] rounded-xl bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#5c5c5c] bg-[#f0efea]">
            <th className="px-4 py-3">Data</th>
            <th className="px-4 py-3">Aluno</th>
            <th className="px-4 py-3">Contato</th>
            <th className="px-4 py-3">Horários sugeridos</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Mensagem</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b) => (
            <tr
              key={b.id}
              data-testid={`booking-row-${b.id}`}
              className="border-t border-[#e5e3db] align-top"
            >
              <td className="px-4 py-3 text-[#5c5c5c] whitespace-nowrap">
                {formatDate(b.created_at)}
              </td>
              <td className="px-4 py-3 font-medium text-[#1e2a5a]">{b.name}</td>
              <td className="px-4 py-3">
                <a
                  href={`mailto:${b.email}`}
                  className="inline-flex items-center gap-1 text-[#1e2a5a] hover:text-[#d9381e]"
                >
                  <Mail className="h-3 w-3" />
                  {b.email}
                </a>
                {b.phone && (
                  <>
                    <br />
                    <a
                      href={`https://wa.me/${b.phone.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[#5c5c5c] hover:text-[#25d366] text-xs"
                    >
                      <MessageCircle className="h-3 w-3" />
                      {b.phone}
                    </a>
                  </>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-[#3a3a3a] whitespace-nowrap">
                <ul className="flex flex-col gap-1">
                  {(b.slots || []).map((s, i) => (
                    <li
                      key={s.start}
                      className={
                        b.confirmed_slot_index === i
                          ? "text-[#2f7a3c] font-medium"
                          : ""
                      }
                    >
                      <Calendar className="inline h-3 w-3 mr-1 opacity-60" />
                      {s.label || s.start}
                      {b.confirmed_slot_index === i && " ✓"}
                    </li>
                  ))}
                </ul>
              </td>
              <td className="px-4 py-3">
                <BookingStatusBadge status={b.status} />
              </td>
              <td className="px-4 py-3 text-[#3a3a3a] max-w-xs text-xs">
                {b.message || <span className="text-[#9c9c9c]">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const QuizTable = ({ items }) => {
  if (items === null) return <Loading />;
  if (items.length === 0)
    return <EmptyState>Nenhum resultado de teste ainda.</EmptyState>;
  return (
    <div className="mt-8 overflow-x-auto border border-[#e5e3db] rounded-xl bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#5c5c5c] bg-[#f0efea]">
            <th className="px-4 py-3">Data</th>
            <th className="px-4 py-3">Nome</th>
            <th className="px-4 py-3">E-mail</th>
            <th className="px-4 py-3">Nível</th>
            <th className="px-4 py-3">Pontuação</th>
            <th className="px-4 py-3">Idioma</th>
          </tr>
        </thead>
        <tbody>
          {items.map((q) => (
            <tr
              key={q.id}
              data-testid={`quiz-row-${q.id}`}
              className="border-t border-[#e5e3db]"
            >
              <td className="px-4 py-3 text-[#5c5c5c] whitespace-nowrap">
                {formatDate(q.created_at)}
              </td>
              <td className="px-4 py-3 font-medium">{q.name}</td>
              <td className="px-4 py-3">
                <a
                  href={`mailto:${q.email}`}
                  className="inline-flex items-center gap-1.5 link-underline"
                >
                  <Mail className="h-3.5 w-3.5" /> {q.email}
                </a>
              </td>
              <td className="px-4 py-3">
                <span
                  className="inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
                  style={{ backgroundColor: LEVEL_COLORS[q.level] || "#1e2a5a" }}
                >
                  {q.level}
                </span>
              </td>
              <td className="px-4 py-3 text-[#5c5c5c]">
                {q.score} / {q.total}
              </td>
              <td className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c]">
                {q.language || "pt"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const STEP_LABELS = {
  1: "D+1 · Dica gramatical",
  2: "D+4 · 7 erros comuns",
  3: "D+9 · História da Marina",
  4: "D+15 · Última mensagem",
};

const SOURCE_BADGE = {
  contact_form: { label: "Contato", color: "#1e2a5a" },
  lead_magnet: { label: "Lead magnet", color: "#d9381e" },
  quiz_result: { label: "Teste nível", color: "#5c8aa6" },
};

const NurtureSummary = ({ data }) => {
  if (!data) {
    return (
      <div className="mt-8 flex items-center gap-2 text-sm text-[#5c5c5c]">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando sequência…
      </div>
    );
  }
  return (
    <div data-testid="nurture-summary" className="mt-8 space-y-10">
      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard
          label="E-mails enviados"
          value={data.total_sent}
          testid="stat-nurture-sent"
        />
        <StatCard
          label="Pendentes na fila"
          value={data.total_pending}
          testid="stat-nurture-pending"
        />
        <StatCard
          label="Descadastrados"
          value={data.total_unsubscribed}
          testid="stat-nurture-unsub"
        />
      </div>

      <section>
        <h2 className="font-display text-2xl text-[#1e2a5a] tracking-tight">
          Próximos envios
        </h2>
        {data.upcoming.length === 0 ? (
          <p className="mt-4 text-sm text-[#5c5c5c]">Nenhum e-mail agendado.</p>
        ) : (
          <NurtureTable items={data.upcoming} dateField="due_at" dateLabel="Sai em" />
        )}
      </section>

      <section>
        <h2 className="font-display text-2xl text-[#1e2a5a] tracking-tight">
          Enviados recentemente
        </h2>
        {data.recently_sent.length === 0 ? (
          <p className="mt-4 text-sm text-[#5c5c5c]">Nenhum e-mail enviado ainda.</p>
        ) : (
          <NurtureTable items={data.recently_sent} dateField="sent_at" dateLabel="Enviado em" />
        )}
      </section>
    </div>
  );
};

const NurtureTable = ({ items, dateField, dateLabel }) => (
  <div className="mt-4 overflow-x-auto bg-white border border-[#e5e3db] rounded-xl">
    <table className="w-full text-sm">
      <thead className="bg-[#f4f1ea] text-left text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
        <tr>
          <th className="px-4 py-3">E-mail</th>
          <th className="px-4 py-3">Etapa</th>
          <th className="px-4 py-3">Origem</th>
          <th className="px-4 py-3">{dateLabel}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, i) => {
          const badge = SOURCE_BADGE[it.source] || { label: it.source || "—", color: "#5c5c5c" };
          return (
            <tr
              key={`${it.email}-${it.step}-${i}`}
              className="border-t border-[#e5e3db]"
            >
              <td className="px-4 py-3">
                <p className="text-[#1e2a5a]">{it.name || it.email.split("@")[0]}</p>
                <p className="text-xs text-[#5c5c5c]">{it.email}</p>
              </td>
              <td className="px-4 py-3 text-[#5c5c5c]">
                <span className="inline-flex items-center gap-1.5">
                  <Send className="h-3.5 w-3.5" />
                  {STEP_LABELS[it.step] || `Step ${it.step}`}
                </span>
              </td>
              <td className="px-4 py-3">
                <span
                  className="inline-block rounded-full px-2.5 py-1 text-[10px] font-medium text-white"
                  style={{ backgroundColor: badge.color }}
                >
                  {badge.label}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-[#5c5c5c]">
                {formatDate(it[dateField])}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
